import DocShell from '@/components/rack/DocShell';
import ContactPanel from '@/components/rack/ContactPanel';

/**
 * 支持。
 *
 * 旧版是浅色玻璃卡片，四张卡里有一张指向 mall.minddock.ai——2026-09-01 实测
 * 连不上（HTTP 000，DNS 解析正常但没有服务）。支持页上挂死链是最伤的一种错误：
 * 客户正是在出问题的时候点进来的。这版删掉它，把「谁回、多久回、怎么找」放在最前面。
 *
 * 主联系方式用 ContactPanel，和首页「联系」是同一个组件——支持页看起来必须是
 * 同阳仔AI的同一台机器。
 *
 * 知识星球与小程序两个入口保留但降级：它们是资料和课程的入口，不是支持通道，
 * 文案里去掉了「官方社区 / 一站式技术服务平台」这类说不出验收标准的说法。
 */

/** 第一次联系时说清这三件事，能省掉三个来回 */
const brief: [string, string, string][] = [
  ['场景', 'CONTEXT', '要解决的是什么事，谁在用，用在哪个环节。'],
  ['现状', 'CURRENT', '现在是怎么做的、卡在哪、已经有什么系统或数据。'],
  ['时间', 'DEADLINE', '什么时候要，是否有硬节点（发布会、审核、开学季…）。'],
];

const faq: [string, string][] = [
  [
    '多久能收到回复？',
    '工作日一天内。周末可能慢一点，但不会没有回音——回你的就是做这件事的人，没有客服中转。',
  ],
  [
    '怎么报价？会不会中途加价？',
    '先把场景和约束聊清楚，然后给书面方案：技术路线、里程碑、固定报价、验收标准一次给全。范围之内不加价；范围要变，先改方案再谈钱。',
  ],
  [
    '能不能只做一部分，或者中途停？',
    '可以。按里程碑分阶段交付，每个阶段交的是能跑起来的东西，不是文档和进度条。你随时可以喊停，停在哪一段，那一段的成果就归你。',
  ],
  [
    '源码给不给？',
    '给。交付时源码、部署脚本、运维说明一并交，附一段保修期。之后你可以让自己的团队接着做，也可以继续找我。',
  ],
  [
    '数据能不能不出内网？',
    '能。做法上本来就偏执「端上能跑的就不上云」——模型推理、动作解算这类都可以留在你自己的机器或内网里，私有化部署是常规选项，不是加钱功能。',
  ],
  [
    '量化能不能承诺收益？',
    '不能。量化只做工程交付：策略实现、回测、风控、执行、监控。不代客理财、不承诺收益、不提供投资建议，站上的净值类图形都是示意数据。',
  ],
  [
    '已经上线的东西出问题了怎么办？',
    '保修期内直接找我修，按问题严重程度排优先级。保修期后可以签维护，也可以按次处理——不会因为项目结束就找不到人。',
  ],
];

/** 已发布产品各自的支持入口。加条目前先确认链接真的能打开 */
const products: [string, string, string][] = [
  ['Anchor', 'iOS · App Store 审核中', '/anchor/support.html'],
];

const extras = [
  {
    name: '知识星球',
    meta: '资料与答疑',
    desc: '免费资源文档、开源项目，以及公开或私信的技术答疑。',
    qr: '/support/knowledge_planet.webp',
  },
  {
    name: '官方小程序',
    meta: '课程与资料',
    desc: '技术资料下载、在线课程，以及一对一顾问服务的入口。',
    qr: '/support/mini_program.webp',
  },
];

export default function Support() {
  return (
    <DocShell
      code="SUPPORT"
      title="支持"
      thesis="回你的就是做这件事的人"
      lede="没有工单系统，没有客服机器人，也没有一层转一层。有问题直接找我本人：合作前的判断、合作中的进度、上线后的故障，走的是同一个入口。"
      description="阳仔AI 支持：微信与邮箱直接联系本人，工作日一天内回复。含报价、分阶段交付、源码交付、私有化部署与保修的常见问题。"
      path="/support"
    >
      <Section eyebrow="CONTACT" title="直接找我" note="扫码加微信是最快的一条；不方便扫码就发邮件。">
        <ContactPanel title="加我的微信" />
      </Section>

      <Section
        eyebrow="BRIEF"
        title="第一次联系，说清这三件事"
        note="不用写得正式，三行就够——说清楚了，我第一条回复就能给你判断，而不是先问你三个问题。"
      >
        <div className="border-t border-rack-bone/12">
          {brief.map(([k, en, v]) => (
            <div
              key={k}
              className="grid items-baseline gap-x-7 gap-y-1.5 border-b border-rack-bone/[0.07] py-4 md:grid-cols-[120px_96px_1fr]"
            >
              <span className="text-[13.5px] font-medium text-rack-bone/90">{k}</span>
              <span className="font-readout text-[9.5px] uppercase tracking-[0.14em] text-rack-bone/26">
                {en}
              </span>
              <span className="text-[13px] leading-[1.9] text-rack-bone/50">{v}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="FAQ" title="常见问题" note="都是被问过的问题，答案和合同里写的是同一套。">
        <div className="border-t border-rack-bone/12">
          {faq.map(([q, a]) => (
            <div
              key={q}
              className="grid gap-x-9 gap-y-2 border-b border-rack-bone/[0.07] py-6 lg:grid-cols-[300px_1fr]"
            >
              <h3 className="flex items-start gap-3 text-[13.5px] font-medium leading-[1.7] text-rack-bone/88">
                <span className="mt-[10px] h-px w-3 shrink-0 bg-rack-amber/60" aria-hidden="true" />
                {q}
              </h3>
              <p className="max-w-[62ch] text-[13px] leading-[1.95] text-rack-bone/52">{a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="PRODUCTS"
        title="已发布产品的支持"
        note="上架的产品各自有独立的支持页，商店审核看的是那一页。"
      >
        <div className="border-t border-rack-bone/12">
          {products.map(([name, meta, href]) => (
            <a
              key={name}
              href={href}
              className="group grid items-baseline gap-x-7 gap-y-1.5 border-b border-rack-bone/[0.07] py-4 transition-colors hover:bg-rack-panel/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rack-amber md:grid-cols-[160px_1fr_auto]"
            >
              <span className="text-[13.5px] font-medium text-rack-bone/90">{name}</span>
              <span className="font-readout text-[10px] uppercase tracking-[0.14em] text-rack-bone/35">
                {meta}
              </span>
              <span className="font-readout text-[12px] tracking-[0.06em] text-rack-bone/40 transition-colors group-hover:text-rack-amber">
                帮助与支持 →
              </span>
            </a>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="EXTRAS"
        title="资料与课程"
        note="不是支持通道——找我修东西请走上面的微信或邮箱，这两个入口是拿资料用的。"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          {extras.map((e) => (
            <div
              key={e.name}
              className="overflow-hidden rounded-[10px] border border-rack-bone/10 bg-rack-panel"
            >
              <div className="flex items-center justify-between gap-3 border-b border-rack-bone/[0.08] bg-rack-chrome px-4 py-2.5">
                <span className="text-[12.5px] font-medium text-rack-bone/85">{e.name}</span>
                <span className="font-readout text-[9.5px] uppercase tracking-[0.14em] text-rack-bone/30">
                  {e.meta}
                </span>
              </div>
              <div className="flex items-center gap-4 p-4">
                <img
                  src={e.qr}
                  alt={`${e.name} 二维码`}
                  loading="lazy"
                  className="h-[96px] w-[96px] shrink-0 rounded-sm border border-rack-bone/10 bg-rack-bone/[0.04] object-contain p-1"
                />
                <p className="min-w-0 text-[12px] leading-[1.85] text-rack-bone/48">{e.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </DocShell>
  );
}

/** 支持页的段落抬头。法务页用 §编号（要能被引用），这页不需要，用眉标 */
function Section({
  eyebrow,
  title,
  note,
  children,
}: {
  eyebrow: string;
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-16">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
        <span className="font-readout text-[10px] uppercase tracking-[0.2em] text-rack-amber/70">
          {eyebrow}
        </span>
        <h2 className="font-songti text-[clamp(1.15rem,2.6vw,1.5rem)] font-semibold tracking-[0.12em] text-rack-bone">
          {title}
        </h2>
      </div>
      <p className="mt-3 max-w-[58ch] text-[12.5px] leading-[1.9] text-rack-bone/40">{note}</p>
      <div className="mt-7">{children}</div>
    </section>
  );
}
